import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import { GLOBAL_PRESETS } from "../../routes/routes";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { toastify } from "../../configs/utils";

// Styled Components
const Container = styled.div`
  padding: 0;
  width: 100%;
  background: white;
  min-height: 100vh;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const Content = styled.div`
  background: #ededed;
  border-radius: 0;
  border: none;
  border-top: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding: 2rem;
  box-shadow: none;
  width: 100%;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 0;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: #3b82f6;
    gap: 0.75rem;
  }
`;

const FormContainer = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(17, 24, 39, 0.08);
`;

const FormTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 2rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  padding: 0.875rem 1rem;
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1f2937;
  background: transparent;
  transition: border-color 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#3b82f6'};
  }

  &:hover {
    border-color: ${props => props.$hasError ? '#ef4444' : '#9ca3af'};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 0.875rem 1rem;
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1f2937;
  background: transparent;
  transition: border-color 0.2s ease;
  width: 100%;
  cursor: pointer;
  outline: none;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#3b82f6'};
  }

  &:hover {
    border-color: ${props => props.$hasError ? '#ef4444' : '#9ca3af'};
  }

  option {
    background: white;
    color: #1f2937;
  }
`;

const ErrorMessage = styled.span`
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.25rem;
  display: block;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.primary {
    background: #3b82f6;
    color: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover:not(:disabled) {
      background: #2563eb;
    }

    &:active:not(:disabled) {
      background: #1d4ed8;
    }
  }

  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }
`;

const StatusMessage = styled.p`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-size: 1.1rem;
`;

const GlobalPresetsForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id && id !== "new";
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreset, setIsLoadingPreset] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    ebayCategory: "",
    ebaySportGameFranchise: "",
    ebayLanguage: "",
    ebayManufacturer: "",
    ebaySet: "",
    ebayAutographed: "",
    ebayYearManufactured: "",
    ebayGraded: "",
    ebayVintage: "",
    ebayCondition: "",
    ebayConditionDescription: "",
    ebayCountry: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const MAX_LENGTH = 100;

  // Label for the category-specific field (Sport/Game/Franchise) shown after a category is selected
  const CATEGORY_SPECIFIC_FIELD_LABEL = {
    "Sport Trading Card": "Sport",
    "Non-Sport Trading Card": "Franchise",
    "CCG Individual Card": "Game",
    "Collectible Sports Sticker Singles": "Sport",
  };

  // Get condition options based on category
  const getConditionOptions = (category) => {
    if (category === "CCG Individual Card") {
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
      ];
    } else if (
      category === "Sport Trading Card" ||
      category === "Non-Sport Trading Card" ||
      category === "Collectible Sports Sticker Singles" ||
      category === "Collectible Non-Sport Sticker Singles"
    ) {
      return [
        "Near Mint or Better",
        "Excellent",
        "Very Good",
        "Poor",
      ];
    } else {
      // Default: show all options if category is not set or unknown
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
        "Excellent",
        "Very Good",
        "Poor",
      ];
    }
  };

  // API Configuration
  const API_BASE_URL =
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || "http://localhost:8000";
  const PRESETS_ENDPOINT = `${API_BASE_URL}/api/v1/cards/preset/`;

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  useEffect(() => {
    if (isEditMode) {
      loadPreset();
    }
  }, [id, isEditMode]);

  // Clear condition if it's not valid for the selected category
  useEffect(() => {
    if (formData.ebayCategory && formData.ebayCondition) {
      const validOptions = getConditionOptions(formData.ebayCategory);
      if (!validOptions.includes(formData.ebayCondition)) {
        handleInputChange("ebayCondition", "");
      }
    }
  }, [formData.ebayCategory]);

  const loadPreset = async () => {
    setIsLoadingPreset(true);
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      let response = await fetch(`${PRESETS_ENDPOINT}${id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(`${PRESETS_ENDPOINT}${id}/`, {
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to load preset: ${response.status}`);
      }

      const preset = await response.json();
      const loadedData = {
        name: preset.name || "",
        ebayCategory: preset.preset_data?.ebayCategory || "",
        ebaySportGameFranchise: preset.preset_data?.ebaySportGameFranchise || "",
        ebayLanguage: preset.preset_data?.ebayLanguage || "",
        ebayManufacturer: preset.preset_data?.ebayManufacturer || "",
        ebaySet: preset.preset_data?.ebaySet || "",
        ebayAutographed: preset.preset_data?.ebayAutographed || "",
        ebayYearManufactured: preset.preset_data?.ebayYearManufactured || "",
        ebayGraded: preset.preset_data?.ebayGraded || "",
        ebayVintage: preset.preset_data?.ebayVintage || "",
        ebayCondition: preset.preset_data?.ebayCondition || "",
        ebayConditionDescription:
          preset.preset_data?.ebayConditionDescription || "",
        ebayCountry: preset.preset_data?.ebayCountry || "",
      };
      
      setFormData(loadedData);
      
      // Validate loaded data for character limits
      const errors = {};
      Object.keys(loadedData).forEach((key) => {
        if (loadedData[key] && loadedData[key].length > MAX_LENGTH) {
          errors[key] = `Maximum ${MAX_LENGTH} characters allowed`;
        }
      });
      setFieldErrors(errors);
    } catch (error) {
      console.error("Error loading preset:", error);
      toast.error("Failed to load preset");
      navigate(GLOBAL_PRESETS);
    } finally {
      setIsLoadingPreset(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Check character limit
    if (value.length > MAX_LENGTH) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: `Maximum ${MAX_LENGTH} characters allowed`,
      }));
    } else {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    navigate(GLOBAL_PRESETS);
  };

  const handleSave = async () => {
    // Check if there are any field errors
    if (Object.keys(fieldErrors).length > 0) {
      toast.error("Please fix all errors before saving");
      return;
    }

    // Check if name is provided
    if (!formData.name.trim()) {
      toast.error("Please provide a preset name");
      return;
    }

    // Check if any field has a value
    const hasAnyValue = Object.entries(formData)
      .filter(([key]) => key !== "name")
      .some(([, value]) =>
        typeof value === "string" ? value.trim() !== "" : false
      );

    if (!hasAnyValue) {
      toast.error("Please fill in at least one field before saving");
      return;
    }

    setIsLoading(true);
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const presetPayload = {
        name: formData.name,
        preset_data: {
          ebayCategory: formData.ebayCategory,
          ebaySportGameFranchise: formData.ebaySportGameFranchise,
          ebayLanguage: formData.ebayLanguage,
          ebayManufacturer: formData.ebayManufacturer,
          ebaySet: formData.ebaySet,
          ebayAutographed: formData.ebayAutographed,
          ebayYearManufactured: formData.ebayYearManufactured,
          ebayGraded: formData.ebayGraded,
          ebayVintage: formData.ebayVintage,
          ebayCondition: formData.ebayCondition,
          ebayConditionDescription: formData.ebayConditionDescription,
          ebayCountry: formData.ebayCountry,
        },
      };

      let response;
      if (isEditMode) {
        // Update existing preset
        response = await fetch(`${PRESETS_ENDPOINT}${id}/`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(presetPayload),
        });
      } else {
        // Create new preset
        response = await fetch(PRESETS_ENDPOINT, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(presetPayload),
        });
      }

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (isEditMode) {
          response = await fetch(`${PRESETS_ENDPOINT}${id}/`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(presetPayload),
          });
        } else {
          response = await fetch(PRESETS_ENDPOINT, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(presetPayload),
          });
        }
      }

      if (response.ok) {
        toast.success(
          `Preset ${isEditMode ? "updated" : "created"} successfully!`
        );
        navigate(GLOBAL_PRESETS);
      } else {
        const errorData = await response.json();
        toast.error(
          `Failed to save preset: ${
            errorData.detail || errorData.message || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error("Error saving preset:", error);
      toast.error("Failed to save preset");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingPreset) {
    return (
      <Container>
        <Content>
          <StatusMessage>Loading preset...</StatusMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <BackButton onClick={handleCancel} style={{ marginBottom: "1rem" }}>
          <ArrowLeft size={18} />
          Back to List
        </BackButton>

        <FormContainer>
          <FormTitle>Preset Details</FormTitle>

          <InputGroup style={{ marginBottom: "2rem" }}>
            <Label>Preset Name *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter preset name"
              maxLength={MAX_LENGTH}
              $hasError={!!fieldErrors.name}
            />
            {fieldErrors.name && <ErrorMessage>{fieldErrors.name}</ErrorMessage>}
          </InputGroup>

          <FormTitle style={{ marginTop: "2rem", fontSize: "1rem" }}>
            eBay Item Specifics
          </FormTitle>

          <Grid2>
            <InputGroup>
              <Label>Category</Label>
              <Select
                value={formData.ebayCategory}
                onChange={(e) =>
                  handleInputChange("ebayCategory", e.target.value)
                }
                $hasError={!!fieldErrors.ebayCategory}
              >
                <option value="">Select Category</option>
                <option value="Sport Trading Card">Sport Trading Card</option>
                <option value="Non-Sport Trading Card">Non-Sport Trading Card</option>
                <option value="CCG Individual Card">CCG Individual Card</option>
                <option value="Collectible Sports Sticker Singles">Collectible Sports Sticker Singles</option>
                <option value="Collectible Non-Sport Sticker Singles">Collectible Non-Sport Sticker Singles</option>
              </Select>
              {fieldErrors.ebayCategory && <ErrorMessage>{fieldErrors.ebayCategory}</ErrorMessage>}
            </InputGroup>
            <InputGroup>
              <Label>Language</Label>
              <Input
                type="text"
                value={formData.ebayLanguage}
                onChange={(e) =>
                  handleInputChange("ebayLanguage", e.target.value)
                }
                maxLength={MAX_LENGTH}
                $hasError={!!fieldErrors.ebayLanguage}
              />
              {fieldErrors.ebayLanguage && <ErrorMessage>{fieldErrors.ebayLanguage}</ErrorMessage>}
            </InputGroup>
          </Grid2>

          {CATEGORY_SPECIFIC_FIELD_LABEL[formData.ebayCategory] && (
            <Grid2>
              <InputGroup>
                <Label>{CATEGORY_SPECIFIC_FIELD_LABEL[formData.ebayCategory]}</Label>
                <Input
                  type="text"
                  value={formData.ebaySportGameFranchise}
                  onChange={(e) =>
                    handleInputChange("ebaySportGameFranchise", e.target.value)
                  }
                  placeholder={`Enter ${CATEGORY_SPECIFIC_FIELD_LABEL[formData.ebayCategory].toLowerCase()}`}
                  maxLength={MAX_LENGTH}
                  $hasError={!!fieldErrors.ebaySportGameFranchise}
                />
                {fieldErrors.ebaySportGameFranchise && <ErrorMessage>{fieldErrors.ebaySportGameFranchise}</ErrorMessage>}
              </InputGroup>
              <div></div>
            </Grid2>
          )}

          <Grid2>
            <InputGroup>
              <Label>Manufacturer</Label>
              <Input
                type="text"
                value={formData.ebayManufacturer}
                onChange={(e) =>
                  handleInputChange("ebayManufacturer", e.target.value)
                }
                maxLength={MAX_LENGTH}
                $hasError={!!fieldErrors.ebayManufacturer}
              />
              {fieldErrors.ebayManufacturer && <ErrorMessage>{fieldErrors.ebayManufacturer}</ErrorMessage>}
            </InputGroup>
            <InputGroup>
              <Label>Set</Label>
              <Input
                type="text"
                value={formData.ebaySet}
                onChange={(e) => handleInputChange("ebaySet", e.target.value)}
                maxLength={MAX_LENGTH}
                $hasError={!!fieldErrors.ebaySet}
              />
              {fieldErrors.ebaySet && <ErrorMessage>{fieldErrors.ebaySet}</ErrorMessage>}
            </InputGroup>
          </Grid2>

          <Grid2>
            <InputGroup>
              <Label>Autographed</Label>
              <Select
                value={formData.ebayAutographed}
                onChange={(e) =>
                  handleInputChange("ebayAutographed", e.target.value)
                }
                $hasError={!!fieldErrors.ebayAutographed}
              >
                <option value="">Select Option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
              {fieldErrors.ebayAutographed && <ErrorMessage>{fieldErrors.ebayAutographed}</ErrorMessage>}
            </InputGroup>
            <InputGroup>
              <Label>Year Manufactured</Label>
              <Input
                type="text"
                value={formData.ebayYearManufactured}
                onChange={(e) =>
                  handleInputChange("ebayYearManufactured", e.target.value)
                }
                maxLength={MAX_LENGTH}
                $hasError={!!fieldErrors.ebayYearManufactured}
              />
              {fieldErrors.ebayYearManufactured && <ErrorMessage>{fieldErrors.ebayYearManufactured}</ErrorMessage>}
            </InputGroup>
          </Grid2>

          <Grid2>
            <InputGroup>
              <Label>Graded</Label>
              <Select
                value={formData.ebayGraded}
                onChange={(e) =>
                  handleInputChange("ebayGraded", e.target.value)
                }
                $hasError={!!fieldErrors.ebayGraded}
              >
                <option value="">Select Option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
              {fieldErrors.ebayGraded && <ErrorMessage>{fieldErrors.ebayGraded}</ErrorMessage>}
            </InputGroup>
            <InputGroup>
              <Label>Vintage</Label>
              <Select
                value={formData.ebayVintage}
                onChange={(e) =>
                  handleInputChange("ebayVintage", e.target.value)
                }
                $hasError={!!fieldErrors.ebayVintage}
              >
                <option value="">Select Option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
              {fieldErrors.ebayVintage && <ErrorMessage>{fieldErrors.ebayVintage}</ErrorMessage>}
            </InputGroup>
          </Grid2>

          <Grid2>
            <InputGroup>
              <Label>Condition</Label>
              <Select
                value={formData.ebayCondition}
                onChange={(e) =>
                  handleInputChange("ebayCondition", e.target.value)
                }
                $hasError={!!fieldErrors.ebayCondition}
              >
                <option value="">Select Condition</option>
                {getConditionOptions(formData.ebayCategory).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
              {fieldErrors.ebayCondition && <ErrorMessage>{fieldErrors.ebayCondition}</ErrorMessage>}
            </InputGroup>
            <div></div>
          </Grid2>

          <Grid2>
            <InputGroup>
              <Label>Condition Description</Label>
              <Input
                type="text"
                value={formData.ebayConditionDescription}
                onChange={(e) =>
                  handleInputChange("ebayConditionDescription", e.target.value)
                }
                maxLength={MAX_LENGTH}
                $hasError={!!fieldErrors.ebayConditionDescription}
              />
              {fieldErrors.ebayConditionDescription && <ErrorMessage>{fieldErrors.ebayConditionDescription}</ErrorMessage>}
            </InputGroup>
            <InputGroup>
              <Label>Country of Origin</Label>
              <Input
                type="text"
                value={formData.ebayCountry}
                onChange={(e) => handleInputChange("ebayCountry", e.target.value)}
                maxLength={MAX_LENGTH}
                $hasError={!!fieldErrors.ebayCountry}
                placeholder="Enter country"
              />
              {fieldErrors.ebayCountry && <ErrorMessage>{fieldErrors.ebayCountry}</ErrorMessage>}
            </InputGroup>
          </Grid2>

          <FormActions>
            <Button
              onClick={handleCancel}
              className="secondary"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="primary"
              disabled={isLoading}
            >
              {isLoading
                ? "Saving..."
                : isEditMode
                ? "Update Preset"
                : "Save Preset"}
            </Button>
          </FormActions>
        </FormContainer>
      </Content>
    </Container>
  );
};

export default GlobalPresetsForm;
